/* eslint-disable sonarjs/cognitive-complexity */
import React, { FC, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile, isMobileOnly } from 'react-device-detect';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import ContinuePlayingNotification from 'src/components/base-components/ContinuePlayingNotification';
import CookiesAcceptor from 'src/components/base-components/CookiesAcceptor';
import GameBoxHeader from 'src/components/base-components/GameBoxHeader';
import MiniGame from 'src/components/base-components/MiniGame';
import GlobalTurnstile from 'src/components/common/Turnstile/GlobalTurnstile';
import Routing from 'src/components/Routes/Routing';
import { GDLibProvider } from 'src/contexts/GDLibContext';
import {
  checkDeviceCompatibility,
  configsVariablesHandler,
  detectDevice,
  envBoolean,
  envStrToBoolean,
  getFreeSpinMessages,
  getFromQueryParams,
  getSkinId,
  isMarketingParam,
  isTokenExpired,
  onInactive,
  scrollToTop,
  setBodyOverflow,
} from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import useGameBox from 'src/hooks/use-gamebox';
import { useGDLIBInit } from 'src/hooks/use-gdlib-Init';
import { useGDLIBLoad } from 'src/hooks/use-gdlib-loadoad';
import { useI18n } from 'src/hooks/use-i18n-translation';
import useMessagesHandler from 'src/hooks/use-messages-handler';
// import useMessagesSocket from 'src/hooks/use-messages-socket';
import { usePersistPaletteLocalStorage } from 'src/hooks/use-persist-palette';
import { usePostMesssage } from 'src/hooks/use-postMessage';
import { useSessionRecorder } from 'src/hooks/use-session-recorder';
import useActivitySocket from 'src/hooks/useActivitySocket';
import useGbMessages from 'src/hooks/useGBMessages';
import { useNavigateToCategory } from 'src/hooks/useNavigateToCategory';
import { useQuery } from 'src/hooks/useQuery';
import { getExternalAppUrl } from 'src/services/authorizationApi';
import { getPallete } from 'src/services/configsApi';
import { getMessages } from 'src/services/gameApi';
import PersisterService from 'src/services/PersisterService';
import { verifyEmail } from 'src/services/userApi';
import { openMyAccountModal } from 'src/store/account-modal/actions';
import { setDeepLink, setSelectedCategory } from 'src/store/app/actions';
import { openAuthModal } from 'src/store/auth-modal/actions';
import { setCategoriesData, setPageConfigs } from 'src/store/configs/actions';
import { openDialog } from 'src/store/dialog/actions';
import { EnumDialogsKeys } from 'src/store/dialog/types';
import { setActualMissionsCount, setWheelUrl } from 'src/store/integrations/actions';
import {
  setBalance,
  setForceLogout,
  setKycStatus,
  setLogout,
  setPhoneCode,
  setReferralLink,
} from 'src/store/user/actions';
import { RootState } from 'src/types/store-types';
import {
  DAY_JS_LOCALE_MAP,
  DEFAULT_INACTIVITY_TIME,
  LEGITIMUZ_TOKEN,
  ONE_HOUR,
  PAPIGAMES_AFFILATES,
  THEMES,
} from 'src/utils/constants';
import { lazyWithCustomError } from 'src/utils/lazyWithCustomError';
import GeneralLoaderPage from '../GeneralLoaderPage';
import LoginPage from '../LoginPage';
import './styles.scss';

const AccountModal = lazyWithCustomError(() => import('src/components/base-components/Modals/AccountModal'));
const InactivityModal = lazyWithCustomError(() => import('src/components/base-components/Modals/InactivityModal'));
const ErrorMessage = lazyWithCustomError(() => import('src/components/base-components/Modals/ErrorMessage'));
const EmailVerificationModal = lazyWithCustomError(
  () => import('src/components/base-components/Modals/EmailVerificationModal')
);
const ForceLogout = lazyWithCustomError(() => import('src/components/base-components/Modals/ForceLogoutModal'));
const Dialogs = lazyWithCustomError(() => import('src/components/base-components/Modals/Dialogs'));

export const Main: FC = () => {
  const navigate = useNavigate();
  const device = detectDevice();
  const deviceCompatibilityStatus = checkDeviceCompatibility().compatible;
  const waitingForUserLogin = useRef(false);
  const initialTimeoutRef: any = useRef(null);
  const hourlyIntervalRef: any = useRef(null);

  const [hoursSpent, setHoursSpent] = useState(() => {
    // Retrieve hoursSpent from localStorage on initial load
    const savedHours = localStorage.getItem('hoursSpent');
    return savedHours ? parseInt(savedHours, 10) : 0;
  });

  usePersistPaletteLocalStorage();
  // useMessagesSocket();
  useMessagesHandler();
  useGbMessages();
  useGameBox();

  const { search, pathname } = useLocation();
  const queries = useQuery();
  const { locale } = useI18n();
  const skinId = getSkinId();
  const isKycEnable = envStrToBoolean(process.env.REACT_APP_KYC_ENABLE);
  const casinoTournamentIframe: any = document.querySelector('iframe#tournament');

  const personalInfo = useSelector((state: RootState) => state.user?.user?.personalInfo);

  const { referralLinks, user, forceLogout, kycStatus } = useSelector((state: RootState) => state.user);

  const sessionRecorder = useSessionRecorder();
  const { deepLink } = useSelector((state: RootState) => state.app);
  const { game } = useSelector((state: RootState) => state.games);
  useActivitySocket(user);

  const urlQuery = getFromQueryParams(search);

  const _logout = useConnectedAction(setLogout);
  const _setCategoriesData = useConnectedAction(setCategoriesData.req);
  const _openAccountModal = useConnectedAction(openMyAccountModal);
  const _setPageConfigs = useConnectedAction(setPageConfigs.req);
  const _setSelectedCategory = useConnectedAction(setSelectedCategory);
  const _setDeepLink = useConnectedAction(setDeepLink);
  const _openAuthModal = useConnectedAction(openAuthModal);
  const _setPhoneCode = useConnectedAction(setPhoneCode);
  const _setKycStatus = useConnectedAction(setKycStatus);
  const _openDialog = useConnectedAction(openDialog);
  const _setReferralLink = useConnectedAction(setReferralLink);
  const _setActualMissionsCount = useConnectedAction(setActualMissionsCount);
  const _setWheelUrl = useConnectedAction(setWheelUrl);
  const _setForceLogout = useConnectedAction(setForceLogout);
  const _requestBalance = useConnectedAction(setBalance.req);
  const { generalLoading } = useSelector((state: RootState) => state.app);
  const { generalConfigs, navigation } = useSelector((state: RootState) => state.configs);
  const [inactivityModalVisibility, setInactivityModalVisibility] = useState<boolean>(false);
  const [verificationModalVisibility, setVerificationModalVisibility] = useState<boolean>(false);
  const [forceLogoutModalVisibility, setForceLogoutModalVisibility] = useState<boolean>(false);
  const [activityTrigger, setActivityTrigger] = useState(false);
  const [openTournamentByHostInitativeFlag, setOpenTournamentByHostInitativeFlag] = useState<boolean>(false);
  const [offInactivityModal, setOffInactivityModal] = useState<boolean>(false);
  const hasFetched = useRef(false);
  const navigateToCategory = useNavigateToCategory();

  const link = useMemo(() => {
    return personalInfo?.referralUrl || referralLinks?.referralUrl;
  }, [personalInfo, referralLinks]);

  const loginPage = useMemo(() => {
    if (envBoolean(process.env.REACT_APP_HAS_LOGIN_PAGE as string) && !user) {
      return <LoginPage />;
    }
    return <></>;
  }, [user]);

  const handleStorageMessages = (event: any): void => {
    if (event.key === 'sessionId' && event.oldValue && !event.newValue) {
      _logout();
    }
  };

  const toggleInactivityVisibility = (): void => {
    setInactivityModalVisibility(!inactivityModalVisibility);
  };

  const toggleVerficationVisibility = (): void => {
    setVerificationModalVisibility(!verificationModalVisibility);
  };

  const toggleForceLogoutModalVisibility = (): void => {
    setForceLogoutModalVisibility(!forceLogoutModalVisibility);
  };

  const handleCloseGameBox = (): void => {
    // This will trigger the closeGameBoxInGameWidget callback registered in use-gamebox.ts
    window.__gdlib?.callRegisteredCB('closeGameBoxInGameWidget', true);
  };

  // comment for club app autologin
  const handleInactivityLogout = (): void => {
    _logout({
      onSuccessCb: () => {
        window.location.href = '/';
        toggleInactivityVisibility();
        _setPageConfigs();
      },
    });
  };

  const clearBannersFromStorage = (): void => {
    sessionStorage.removeItem('homeGrid');
    sessionStorage.removeItem('banners');
  };

  const navigateToLogin = async (): Promise<void> => {
    if (user) {
      getExternalAppUrl({ token: user.token, authClientAppId: queries.authClientAppId }).then((res: any) => {
        if (res?.result?.authClientUrl) {
          _openDialog({
            dialogType: EnumDialogsKeys.DINHU_ANALIZER,
            dialogProps: { data: res?.result?.authClientUrl },
          });
        }
      });
    } else {
      !pathname.includes('signup') && _openAuthModal('login');
    }
  };

  // Carries marketing params (utm_*, gclid, ...) through the affiliate signup redirect.
  const buildAffiliateSignUpUrl = (affiliateId: string): string => {
    const current = new URLSearchParams(search);
    const params = new URLSearchParams({ affiliateId: current.get('affiliateId') ?? affiliateId });

    current.forEach((value, key) => {
      if (isMarketingParam(key)) {
        params.append(key, value);
      }
    });

    return `/signup?${params.toString()}`;
  };

  const navigateToDeepLinkReg = (): void => {
    if (process.env.REACT_APP_WEBSITENAME === 'PapiGames' && urlQuery.signUpBonusCode) {
      sessionStorage.setItem('signUpBonusCode', urlQuery.signUpBonusCode);
      if (user && isTokenExpired()) {
        navigateToCategory();
      } else {
        setBodyOverflow('set');
        navigate('/signup');
        scrollToTop();
        _setDeepLink('registrationModal');
      }
    }
    if (process.env.REACT_APP_WEBSITENAME === 'PapiGames' && urlQuery.signInBonusCode) {
      sessionStorage.setItem('signInBonusCode', urlQuery.signInBonusCode);
      if (user && isTokenExpired()) {
        navigateToCategory();
      } else {
        setBodyOverflow('set');
        _openAuthModal('login');
      }
    }
    if (urlQuery.affiliateId) {
      if (user && isTokenExpired()) {
        navigateToCategory();
      } else {
        setBodyOverflow('set');
        if (!pathname.includes('signup')) {
          navigate(buildAffiliateSignUpUrl(urlQuery.affiliateId));
        }
        scrollToTop();
        _setDeepLink('registrationModal');
      }
    }
    if (process.env.REACT_APP_WEBSITENAME === 'PapiGames' && urlQuery.trackerID) {
      sessionStorage.setItem('trackerID', urlQuery.trackerID);
    }
  };

  const navigateToDeepLink = (): void => {
    switch (urlQuery.navigateTo) {
      case 'provider':
        if (urlQuery.providername) {
          _setSelectedCategory('casino');
          navigate(`/category/casino/${search}`);
        }
        break;
      case 'privacy':
        setTimeout(() => {
          _openAccountModal({ section: 'info_and_rules', subSection: 'policies', fullHeight: true });
        }, 1000);
        _setSelectedCategory('home');
        break;
      case 'register':
        if (!user) {
          setBodyOverflow('set');
          navigate('/signup');
          scrollToTop();
        }
        break;
      case 'login':
        if (!user) {
          _openAuthModal('login');
        }
        break;
      case 'GoS':
        if (user) {
          if (!pathname.includes('casino-tournament')) {
            navigate(`/category/casino-tournament/${search}`);
            _setSelectedCategory('casinoTournament');
          }
          setOpenTournamentByHostInitativeFlag(true);
        } else {
          _openAuthModal('login');
        }

        break;
      case 'liveChat':
        _setDeepLink('liveChat');
        navigateToCategory();
        break;
      case 'deposit':
        // if (urlQuery.amount && typeof Number(urlQuery.amount) === 'number' && !isNaN(Number(urlQuery.amount))) {
        //   sessionStorage.setItem('deepDepositValue', urlQuery.amount);
        // }
        if (user) {
          _setDeepLink('deposit');
          navigateToCategory();
        } else {
          _openAuthModal('login');
          _setDeepLink('deposit');
        }
        break;
      case 'raf':
        if (user) {
          _setReferralLink({ referralUrl: link, referralId: personalInfo?.referralId || 0 });
          _openAccountModal({ section: 'referAFriend', subSection: 'referAFriend' });
        } else {
          _openAuthModal('login');
          _setDeepLink('raf');
        }
        break;
      default:
        if (!pathname.includes('signup')) {
          navigateToCategory();
        }
        break;
    }
  };

  const setPalette = (): void => {
    let _res = null;
    let _theme = '';

    if (localStorage.getItem('selectedPalette')) {
      _theme = localStorage.getItem('selectedPalette') as string;
    }

    if (PersisterService.hasPersistValue('palette')) {
      _res = PersisterService.getPersistValue('palette');
      // TO DO change palete name
      const t = _res ? process.env.REACT_APP_DEFAULT_PALLETE : THEMES[0];
      const palette = Boolean(_theme) ? _res[_theme] : t;
      configsVariablesHandler(palette);
      localStorage.setItem('selectedPalette', Boolean(_theme) ? _theme : Object.keys(_res)[0]);
    } else {
      getPallete().then((res: any) => {
        if (res.result) {
          _res = res.result;
          const palette = Boolean(_theme) ? _res[_theme] : _res[process.env.REACT_APP_DEFAULT_PALLETE as string];
          PersisterService.setPersistValue('palette', res.result);
          configsVariablesHandler(palette);
          localStorage.setItem(
            'selectedPalette',
            Boolean(_theme) ? _theme : (process.env.REACT_APP_DEFAULT_PALLETE as string)
          );
        }
      });
    }
  };

  useEffect(() => {
    if (casinoTournamentIframe && openTournamentByHostInitativeFlag) {
      setTimeout(() => {
        casinoTournamentIframe?.contentWindow.postMessage(
          { type: 'openDialogByHostInitiative', dialogType: 'tournamentRegister', tournamentId: urlQuery.tourId },
          '*'
        );
        setOpenTournamentByHostInitativeFlag(false);
      }, 1000);
    }
    window.addEventListener('message', (e) => {
      if (e.data?.name === 'clickAction') {
        setOffInactivityModal(false);
        setActivityTrigger((prevState) => !prevState);
      } else if (e.data?.name === 'offInactivity') {
        setOffInactivityModal(true);
      }
    });
  }, [casinoTournamentIframe, openTournamentByHostInitativeFlag]);

  useEffect(() => {
    if (!user) {
      localStorage.removeItem('sessionTime');
      return;
    }
    // Start the timer
    const intervalId = setInterval(() => {
      const savedTime = parseInt(localStorage.getItem('sessionTime') as string, 10) || 0;
      const updatedTime = savedTime + 1;
      localStorage.setItem('sessionTime', updatedTime.toString());
      return updatedTime;
    }, 1000);

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, [user]);

  useEffect(() => {
    if (!user || kycStatus) return;

    if (user.kyc && Object.keys(user.kyc).length > 0) {
      _setKycStatus(user.kyc.verificationStatus);
    } else {
      _setKycStatus(undefined);
    }
  }, [kycStatus, user]);

  useEffect(() => {
    if (user && !inactivityModalVisibility && generalConfigs && !offInactivityModal) {
      onInactive(
        () => setInactivityModalVisibility(true),
        () => setInactivityModalVisibility(false),
        +generalConfigs?.inactivitiTime || DEFAULT_INACTIVITY_TIME
      );
    }
  }, [user, inactivityModalVisibility, generalConfigs, activityTrigger, offInactivityModal]);

  useEffect(() => {
    if (!user) {
      hasFetched.current = false;
    }
  }, [user]);

  useEffect(() => {
    if (envStrToBoolean(process.env.REACT_APP_MESSAGING_LIB_ENABLED) && process.env.REACT_APP_MESSAGES_URL) {
      if (user && !hasFetched.current) {
        hasFetched.current = true;
        getMessages(user.id).then((res: any) => {
          const messages = getFreeSpinMessages(res?.result?.messages[user.id]);

          if (!!messages?.length) {
            _openDialog({
              dialogType: EnumDialogsKeys.FREE_SPINS,
              dialogProps: { data: messages[0] },
            });
          }
        });

        (window as any)._smartico_user_id = user.id;
        (window as any)._smartico_language = localStorage.selectedLanguage;
      } else {
        (window as any)._smartico_user_id = null;
        (window as any)._smartico_language = null;
      }
    }
  }, [user]);

  useEffect(() => {
    if (!envBoolean(process.env.REACT_APP_HAS_GENERAL_LOADER as string)) {
      _setPageConfigs();
      _setCategoriesData();
    }

    setPalette();

    window.addEventListener('storage', handleStorageMessages);
    return () => window.removeEventListener('storage', handleStorageMessages);
  }, [locale]);

  useEffect(() => {
    const queryParams = new URLSearchParams(search);
    const verificationKey = queryParams.get('verificationKey');
    if (verificationKey) {
      verifyEmail({ key: verificationKey });
      _openDialog({ dialogType: EnumDialogsKeys.SECCESSFULLY_REGED, dialogProps: { data: null } });
    }

    navigateToDeepLink();
    if (search.includes('authClientAppId')) {
      navigateToLogin().catch((e) => console.log(e));
      if (pathname.includes('signup')) {
        const authClientAppId = queryParams.get('authClientAppId');
        sessionStorage.setItem('authClientAppId', String(authClientAppId));
      }
    }
    if (!search.includes('provider')) {
      navigateToDeepLinkReg();
    }
    window.onbeforeunload = clearBannersFromStorage;
  }, []);

  // Once navigation loads (async, after the deep-link run), re-resolve the URL slug so selectedCategory is the name.
  useEffect(() => {
    if (navigation?.length && pathname.includes('category')) {
      navigateToCategory();
    }
  }, [navigation]);

  useEffect(() => {
    if (!!generalConfigs) {
      document?.body.setAttribute('class', `${device} ${generalConfigs?.template}`);
      _setPhoneCode(generalConfigs?.defaultPhoneCode);
    }
  }, [generalConfigs]);

  useEffect(() => {
    if (!!forceLogout?.message) {
      setForceLogoutModalVisibility(true);
    }
  }, [!!forceLogout?.message]);

  useEffect(() => {
    if (isMobile && deepLink === 'raf' && user) {
      setTimeout(() => {
        _openAccountModal({ section: 'referAFriend', subSection: 'referAFriend' });
        _setDeepLink('');
      }, 0);
    }
  }, [deepLink, user]);

  useEffect(() => {
    if (envStrToBoolean(process.env.REACT_APP_ENABLE_NOTIFICATIONS)) {
      // Check if the browser supports notifications
      if ('Notification' in window && typeof Notification.requestPermission === 'function') {
        const permissionResult = Notification.requestPermission() as any;

        if (permissionResult && typeof permissionResult.then === 'function') {
          // Modern browsers (Promise-based)
          permissionResult.then((permission: NotificationPermission) => {
            if (permission === 'denied') {
              console.log('Permission for notifications was denied.');
            }
          });
        } else {
          // Old Safari (returns string directly)
          if (permissionResult === 'denied') {
            console.log('Permission for notifications was denied.');
          }
        }
      } else {
        console.log('This browser does not support desktop notifications.');
      }
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    // Function to initialize and mount the SDK
    const initializeSDK = (): void => {
      if (typeof window === 'undefined') return;

      const LegitSDK = (window as any)?.LegitimuzAntiFraude;
      if (!LegitSDK || !LEGITIMUZ_TOKEN) return;

      const sdkInstance = LegitSDK({
        apiURL: 'https://api.legitimuz.com',
        token: LEGITIMUZ_TOKEN,
        action: 'signin',
        enableRequestGeolocation: true,
      });

      if (sdkInstance) {
        sdkInstance.mount();
        sdkInstance.sendAnalisys({ cpf: user?.nationalId || user?.personalInfo?.nationalId });
      } else {
        console.error('SDK Instance is undefined or could not be initialized');
      }
    };

    const executeSDKWithInterval = (): void => {
      if (!isMounted) return;

      initializeSDK();

      // Schedule the next call in 30 minutes
      const THIRTY_MINUTES = 1800000; // 30 minutes in milliseconds
      setTimeout(executeSDKWithInterval, THIRTY_MINUTES);
    };

    // Only start execution if conditions are met
    if (user && isKycEnable) {
      executeSDKWithInterval();
    }

    return () => {
      isMounted = false; // Prevent further execution after unmount
    };
  }, [user, isKycEnable]);

  const getAccoutnMissinos = async (token: string): Promise<void> => {
    const res = await window.__gdlib!.getMissionsCount(token, process.env.REACT_APP_MODE as GDLibEnvMode);
    if (res) {
      _setActualMissionsCount(res);
    }
  };

  const getMiniGamesList = async (token: string): Promise<void> => {
    const res = await window.__gdlib!.getMiniGamesList(token, process.env.REACT_APP_MODE as GDLibEnvMode);
    if (res) {
      _setWheelUrl(res);
    }
  };

  const initializeGDLib = useCallback(
    async (force = false): Promise<void> => {
      const gdlib = window.__gdlib!;

      console.log('[Main] initializeGDLib called, isInitialized:', gdlib.isInitialized, 'force:', force);

      // Skip re-initialization unless forced (needed for retry when iframe is broken)
      if (gdlib.isInitialized && !force) {
        console.log('[Main] GDLib already initialized, skipping...');
        return;
      }

      if (force && gdlib.isInitialized) {
        console.warn(
          '[Main] ⚠ Force re-initializing GDLib. Status 0 error may occur (non-critical, caused by duplicate init)'
        );
      }

      console.log('[Main] Initializing GDLib...');
      gdlib.init({
        websiteName: process.env.REACT_APP_WEBSITENAME ?? '',
        isGameboxSdk: !envStrToBoolean(process.env.REACT_APP_GAMEBOX_FRAME),
        enableGameboxV3Header: envStrToBoolean(process.env.REACT_APP_GAMEBOX_V3_WEBSITE_HEADER),
        showDepositBtn: envStrToBoolean(process.env.REACT_APP_SHOW_INGAME_DEPOSIT_BTN),
        skinId: skinId,
        language: locale || localStorage.getItem('selectedLanguage'),
        environment: process.env.REACT_APP_MODE,
        logo: urlQuery.logo
          ? decodeURIComponent(urlQuery.logo)
          : generalConfigs?.logo || `${window.location.origin}/images/logo.svg`,
        theme: 'dark',
        showThemeSwitcher: false,
      });

      gdlib.registerCallBack('openLoginForm', 'main', () => {
        _openAuthModal('login');
        isMobileOnly && setBodyOverflow('unset');
      });

      gdlib.registerCallBack('openDepositModal', 'main', () => {
        _openAccountModal({ section: 'deposit', subSection: 'deposit' });
        isMobileOnly && setBodyOverflow('unset');
      });

      gdlib.registerCallBack('missionsCount', 'main', _setActualMissionsCount);
      gdlib.registerCallBack('forceLogout', 'main', _setForceLogout);
      gdlib.registerCallBack('updateBalance', 'main', _requestBalance);

      gdlib.registerCallBack('depositCompleted', 'main', () => {
        _requestBalance();
      });

      gdlib.registerCallBack('updateKycStatus', 'main', (response: { status: string }) => {
        const _userJson = localStorage.getItem('user') as string;
        const _user = JSON.parse(_userJson);
        if (response.status) {
          localStorage.setItem(
            'user',
            JSON.stringify({
              ..._user,
              kyc: {
                accountId: _user.id,
                verificationStatus: response.status,
              },
            })
          );
          _setKycStatus(response.status);
        }
      });

      gdlib.registerCallBack('playerLimitMessage', 'main', (message: any) => {
        if (message.templateId.includes('alert')) {
          _openDialog({
            dialogType: EnumDialogsKeys.LIMITS_EXCEED_ALERT,
            dialogProps: { data: message },
          });
        } else if (message.templateId.includes('block')) {
          _openDialog({
            dialogType: EnumDialogsKeys.LIMITS_EXCEED_BLOCK,
            dialogProps: { data: message },
          });
        }
      });

      if (isKycEnable) {
        gdlib.registerCallBack('badGeoLocation', 'main', (response: any) => {
          _openDialog({
            dialogType: EnumDialogsKeys.BAD_GEOLOCATION,
            dialogProps: { data: { country: response.country, city: response.city } },
          });
        });
      }
    },
    [user]
  );

  const authenticateInGDLib = useCallback(async (): Promise<void> => {
    const gdlib = window.__gdlib!;

    if (user && gdlib) {
      gdlib.login(user.token);
      getAccoutnMissinos(user.token).catch((e) => console.log(e));
      getMiniGamesList(user.token).catch((e) => console.log(e));
    }
  }, [user]);

  const setGDLiblanguage = useCallback(async (): Promise<void> => {
    const gdlib = window.__gdlib!;
    const dayjsLocale = DAY_JS_LOCALE_MAP[locale] || locale;
    dayjsLocale && import(`dayjs/locale/${dayjsLocale}.js`);
    gdlib.changeLanguage(locale);
  }, [locale]);

  useGDLIBLoad(initializeGDLib);

  useGDLIBInit(authenticateInGDLib);

  useGDLIBInit(setGDLiblanguage);

  // Listen for gamebox-widget retry initialization from GameCard
  useEffect(() => {
    const handleGameBoxRetryInit = (event: any): void => {
      const { retryCount } = event.detail || {};
      console.log('event = ', event);
      console.log(`[Main] ========== Received gamebox-retry-init (attempt #${retryCount}) ==========`);

      const gdlib = window.__gdlib;
      const gameboxIframe = document.getElementById('gamebox-widget');
      const gameboxUrl = sessionStorage.getItem('gamebox-widget_url');
      const gameboxModule = gdlib?.['gamebox-widget_module'];

      console.log('[Main] GDLib initialized:', gdlib?.isInitialized);
      console.log('[Main] GameBox iframe exists:', !!gameboxIframe);
      console.log('[Main] GameBox URL in sessionStorage:', !!gameboxUrl);
      console.log('[Main] GameBox module config:', gameboxModule);
      console.log('[Main] GameBox ready:', gameboxModule?.isGameBoxReady);

      // Scenario 1: GDLib not initialized yet
      if (gdlib && !gdlib.isInitialized) {
        console.log('[Main] ⚠ GDLib not initialized yet. Initializing now...');
        initializeGDLib().catch((error) => {
          console.error('[Main] ✗ Error during GDLib initialization:', error);
        });
        return;
      }

      // Scenario 2: GDLib initialized but no URL yet (API call still pending)
      if (gdlib?.isInitialized && !gameboxUrl) {
        console.log('[Main] ⚠ GameBox URL not fetched yet. Module URLs API call may still be pending.');
        console.log('[Main] This usually resolves automatically. Please wait a moment and retry.');
        return;
      }

      // Scenario 3: URL exists but iframe doesn't (setup failed or didn't run)
      if (gameboxUrl && !gameboxIframe) {
        console.error('[Main] ✗ GameBox URL exists but iframe not created!');
        console.log('[Main] URL:', gameboxUrl);
        console.log('[Main] This indicates the setupGameboxWidget() may have failed.');
        console.log('[Main] Check gdRoot.js handleModule() and setupGameboxWidget() for errors.');
        console.log('[Main] Note: setupGameboxWidget is called automatically by handleModule');
        console.log('[Main] Check if there are any errors in console from gdRoot.js');
        return;
      }

      // Scenario 4: Iframe exists but not ready
      if (gameboxIframe && !gameboxModule?.isGameBoxReady) {
        console.log("[Main] ℹ GameBox iframe exists but hasn't sent GAME_BOX_IS_READY message yet.");
        console.log('[Main] The iframe may still be loading. GameCard will poll for readiness.');
        return;
      }

      // Scenario 5: Everything is ready
      if (gameboxIframe && gameboxModule?.isGameBoxReady) {
        console.log('[Main] ✓ GameBox is ready! Game should launch now.');
        return;
      }

      console.log('[Main] Unknown state. Full diagnostic info logged above.');
    };

    window.addEventListener('gamebox-retry-init', handleGameBoxRetryInit);

    return () => {
      window.removeEventListener('gamebox-retry-init', handleGameBoxRetryInit);
    };
  }, [initializeGDLib]);

  // FOR PAPIGAMES streamers affilliateId
  useEffect(() => {
    const affilate = PAPIGAMES_AFFILATES.find((affiliate) => pathname.includes(affiliate.name));
    if (affilate) {
      const id: string = affilate.id;

      if (user && isTokenExpired()) {
        navigateToCategory();
      } else {
        if (!pathname.includes('provider')) {
          setBodyOverflow('set');
          window.location.replace(buildAffiliateSignUpUrl(id));
          scrollToTop();
          _setDeepLink('registrationModal');
        }
      }
    }
  }, [pathname, user]);

  useEffect(() => {
    if (envStrToBoolean(process.env.REACT_APP_DEVICE_COMPATIBILITY_ENABLE) && !deviceCompatibilityStatus) {
      _openDialog({
        dialogType: EnumDialogsKeys.DEVICE_COMPATIBILITY_POPUP,
        dialogProps: { data: {} },
      });
    }
  }, [deviceCompatibilityStatus]);

  // Set up a 3-hour initial delay, then hourly alerts if the user is signed in
  useEffect((): any => {
    if (envStrToBoolean(process.env.REACT_APP_ACTIVITY_TRACKER)) {
      if (user) {
        if (hoursSpent >= 3) {
          // If already past the 3-hour mark, immediately set up the hourly interval
          hourlyIntervalRef.current = setInterval(() => {
            setHoursSpent((prevHours) => {
              const newHours = prevHours + 1;
              _openDialog({
                dialogType: EnumDialogsKeys.ACTIVITY_POPUP,
                dialogProps: { data: newHours },
              });
              localStorage.setItem('hoursSpent', JSON.stringify(newHours));
              return newHours;
            });
          }, ONE_HOUR); // 1 hour
        } else {
          // Initial 3-hour delay for first-time users
          initialTimeoutRef.current = setTimeout(() => {
            _openDialog({
              dialogType: EnumDialogsKeys.ACTIVITY_POPUP,
              dialogProps: { data: 3 },
            });
            setHoursSpent(3);
            localStorage.setItem('hoursSpent', JSON.stringify(3));

            // After first alert, start hourly intervals
            hourlyIntervalRef.current = setInterval(() => {
              setHoursSpent((prevHours) => {
                const newHours = prevHours + 1;
                _openDialog({
                  dialogType: EnumDialogsKeys.ACTIVITY_POPUP,
                  dialogProps: { data: newHours },
                });
                localStorage.setItem('hoursSpent', JSON.stringify(newHours));
                return newHours;
              });
            }, ONE_HOUR); // 1 hour
          }, (3 - hoursSpent) * ONE_HOUR); // Remaining time until 3-hour mark
        }

        // Cleanup on component unmount or user change
        return () => {
          clearTimeout(initialTimeoutRef.current);
          clearInterval(hourlyIntervalRef.current);
        };
      } else {
        // Clear storage and intervals on logout
        clearTimeout(initialTimeoutRef.current);
        clearInterval(hourlyIntervalRef.current);
        localStorage.removeItem('hoursSpent');
        setHoursSpent(0);
      }
    }
  }, [user, _openDialog, setHoursSpent]);

  const identifyRecordedUser = useCallback(async () => {
    if (user) {
      sessionRecorder?.identifyUser(user.id, user.username);

      if (waitingForUserLogin.current) {
        sessionRecorder?.trackEvent('Login complete');
        waitingForUserLogin.current = false;
      }
    } else {
      waitingForUserLogin.current = true;
    }
  }, [user]);

  useEffect(() => {
    identifyRecordedUser();
  }, [identifyRecordedUser]);

  useEffect(() => {
    if ((window as any)?.registerCB) {
      (window as any)?.registerCB('playerLimitMessage', (message: any) => {
        if (message.templateId.includes('alert')) {
          _openDialog({
            dialogType: EnumDialogsKeys.LIMITS_EXCEED_ALERT,
            dialogProps: { data: message },
          });
        } else if (message.templateId.includes('block')) {
          _openDialog({
            dialogType: EnumDialogsKeys.LIMITS_EXCEED_BLOCK,
            dialogProps: { data: message },
          });
        }
      });
    }
  }, []);

  usePostMesssage((v: any) => configsVariablesHandler(v), 'pallete');

  return (
    <GDLibProvider initializeGDLib={initializeGDLib}>
      <GlobalTurnstile />
      {loginPage}
      {envBoolean(process.env.REACT_APP_HAS_GENERAL_LOADER as string) && !generalLoading.includes(false) && (
        <GeneralLoaderPage />
      )}

      <Suspense fallback={null}>
        <AccountModal />
        <ErrorMessage />
        <EmailVerificationModal open={verificationModalVisibility} onCancel={toggleVerficationVisibility} />
        <ForceLogout open={forceLogoutModalVisibility} onClose={toggleForceLogoutModalVisibility} />
        <Dialogs />

        {user && inactivityModalVisibility && !offInactivityModal && (
          <InactivityModal
            doAction={handleInactivityLogout}
            doActivity={toggleInactivityVisibility}
            open={inactivityModalVisibility}
          />
        )}
      </Suspense>

      {/* {gameBoxMessaging} */}

      {/* GameBox Header - shown when game is open in InGame mode (only on desktop when GAMEBOX_V3_HEADER is enabled) */}
      {game && !isMobile && envStrToBoolean(process.env.REACT_APP_GAMEBOX_V3_WEBSITE_HEADER) && (
        <GameBoxHeader onClose={handleCloseGameBox} />
      )}

      <Routing />
      {user && <MiniGame />}
      {user && envStrToBoolean(process.env.REACT_APP_CONTINUE_PLAYING_ENABLE) && <ContinuePlayingNotification />}
      {envStrToBoolean(process.env.REACT_APP_USE_COOKIES) && <CookiesAcceptor />}
    </GDLibProvider>
  );
};
